import { useEffect, useState } from 'react';

import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import { analytics } from '../../../../services/analytics';
import { postRequest } from '../../../../services/api';
import type { ApiResponse } from '../../../../services/types';
import { EnumOpenDialog } from '../../../../stores/ui/types';
import { useStores } from '../../../../stores/useStores';
import Button from '../../../buttons/Button';
import { Dialog } from '../../../Dialog/Dialog';
import { DialogBox } from '../../../Dialog/DialogBox';
import { DialogContent } from '../../../Dialog/DialogContent';
import { DialogHeader } from '../../../Dialog/DialogHeader';
import { FormInput } from '../../../FormInput/FormInput';
import { InfoIcon } from '../../../SVGIcons/InfoIcon';
import Tooltip from '../../../Tooltip';

import { PROMO_CODE_ERROR_MESSAGE } from './consts';
import styles from './PromoCodeInputDialog.module.scss';
interface ValidatePromoCodeResponse {
  isValid: boolean;
  eventTriggerId: string;
}

const PromoCodeInputDialog = observer(() => {
  const { uiStore, eventTriggerStore } = useStores();
  const { t } = useTranslation();
  const isOpen = uiStore.openDialog.dialog === EnumOpenDialog.isOpenPromoCodeDialog;
  const [promoCode, setPromoCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleClose = () => {
    uiStore.setCloseDialog();
  };

  const sendApplyPromoCodeEvent = (success: boolean) => {
    void analytics.sendEvent('promo_code_apply', {
      action: 'click',
      event_category: 'promocode',
      promoCode,
      click_location: 'apply',
      status: success ? 'success' : 'failed',
    });
  };

  const handleSubmit = async () => {
    setError('');
    setIsSubmitting(true);
    try {
      const response = await postRequest<ApiResponse<ValidatePromoCodeResponse>>('/v2/promo-code/validate', {
        promoCode,
      });
      const { isValid } = response.data.data;
      if (isValid) {
        await eventTriggerStore.fetchWithPromoCode(promoCode);
        sendApplyPromoCodeEvent(true);
        handleClose();
        setPromoCode('');
      } else {
        sendApplyPromoCodeEvent(false);
        setError(PROMO_CODE_ERROR_MESSAGE);
        setIsSubmitting(false);
      }
    } catch (err) {
      sendApplyPromoCodeEvent(false);
      setError(PROMO_CODE_ERROR_MESSAGE);
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    void analytics.sendEvent('promo_code_screen', {
      action: 'impression',
      event_category: 'promocode',
    });
  }, [isOpen]);

  return (
    <Dialog classNameBody={styles.promoCodeDialog} isOpen={isOpen}>
      <DialogBox>
        <DialogHeader title={t('rewards.promoCode')} titleClassName={styles.promoTitle} onClose={handleClose} />
        <DialogContent className={styles.promoCodeContainer}>
          <div className={styles.content}>
            <div className={styles.inputContainer}>
              <FormInput
                autoFocus
                dataTestId="promo-code-input"
                maxLength={20}
                placeholder={t('forms.enterPromoCode')}
                type="text"
                value={promoCode}
                onChange={(e) => {
                  setPromoCode(e.target.value.trim());
                  if (error) setError('');
                }}
              />
              {error && (
                <div className={styles.errorTooltipAnchor}>
                  <Tooltip content={error} offset={[18, 15]} placement="top-end" trigger="hover">
                    <div className={styles.errorIcon}>
                      <InfoIcon className={styles.errorIconSvg} color="red" dataTestId="invalid-promo-code-icon" />
                    </div>
                  </Tooltip>
                </div>
              )}
            </div>
            <div className={styles.buttonContainer}>
              <Button
                btnType={error ? 'locked' : 'main'}
                data-testid="promo-code-submit-btn"
                disabled={isSubmitting || !promoCode.trim() || !!error}
                onClick={handleSubmit}
              >
                {isSubmitting ? t('buttons.applying') : t('common.applyUpperCase')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </DialogBox>
    </Dialog>
  );
});

export default PromoCodeInputDialog;
